import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  RefreshCw,
  Users,
  TrendingUp,
  ClipboardCheck,
  Scale,
  FileText,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api, { formatApiError, API } from "../../lib/api";
import { SURVEY_QUESTIONS, SURVEY_DISCLAIMER } from "../../data/ncuaSurvey";
import SampleProgressCard from "./SampleProgressCard";

/**
 * Admin → Survey tab.
 *
 * Visualizes the 10-question NCUA Potential Member Survey responses as
 * horizontal bar charts, surfaces the headline demand metrics the NCUA
 * Form 9500 reviewer looks for (join-likelihood, bank-satisfaction),
 * and offers a one-click PDF export of the whole dashboard for the
 * Economic Justification appendix.
 *
 * Backend: GET /api/admin/credit-union/survey-summary → { total_responses, counts }
 *          GET /api/admin/credit-union/survey-report.pdf → PDF stream
 */
export default function SurveyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/credit-union/survey-summary");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load survey summary");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const downloadPdf = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("bdu_token");
      const resp = await fetch(`${API}/admin/credit-union/survey-report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `BDU_NCUA_Potential_Member_Survey_Report_${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Form 9500 Attachment downloaded");
    } catch (e) {
      toast.error(`PDF export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const total = data?.total_responses || 0;
  const counts = data?.counts || {};

  const headline = useMemo(() => computeHeadline(counts, total), [counts, total]);

  return (
    <div className="space-y-8" data-testid="admin-survey-tab">
      <SampleProgressCard />
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <ClipboardCheck size={12} className="text-bdu-gold" /> NCUA Potential Member Survey · Form 9500 Attachment
          </div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream">
            Potential-Member Demand Dashboard
          </h3>
          <p className="mt-1 text-xs text-bdu-cream/55 max-w-2xl leading-relaxed">
            Aggregated responses from every charter-supporter signup. Each
            tally shows the count and share of respondents selecting that
            option (checkbox totals may exceed 100% as respondents can
            select multiple).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="admin-survey-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={downloadPdf}
            disabled={exporting || total === 0}
            className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-survey-pdf-btn"
          >
            {exporting ? (
              <>
                <RefreshCw size={14} className="animate-spin" /> Preparing PDF…
              </>
            ) : (
              <>
                <Download size={14} /> Form 9500 Attachment (PDF)
              </>
            )}
          </button>
        </div>
      </div>

      {/* Headline stat tiles */}
      <div className="grid sm:grid-cols-4 gap-4" data-testid="admin-survey-headlines">
        <HeadlineTile
          icon={Users}
          label="Total Responses"
          value={total.toLocaleString()}
          sub="Captured on /credit-union signup"
          testId="admin-survey-stat-total"
        />
        <HeadlineTile
          icon={TrendingUp}
          label="Likely to Join"
          value={`${headline.likelyPct}%`}
          sub={`${headline.likelyCount.toLocaleString()} said Very / Somewhat Likely`}
          testId="admin-survey-stat-likely"
          gold
        />
        <HeadlineTile
          icon={Scale}
          label="Inadequately Served"
          value={`${headline.inadequatePct}%`}
          sub={`${headline.inadequateCount.toLocaleString()} say banks fail the metro`}
          testId="admin-survey-stat-inadequate"
        />
        <HeadlineTile
          icon={FileText}
          label="Mission-Driven"
          value={`${headline.missionPct}%`}
          sub={`chose "community investment" as #1 reason`}
          testId="admin-survey-stat-mission"
          gold
        />
      </div>

      {/* Compliance disclaimer */}
      <div className="border border-bdu-gold/20 bg-bdu-gold/5 p-4 text-xs text-bdu-cream/70 italic leading-relaxed" data-testid="admin-survey-disclaimer">
        {SURVEY_DISCLAIMER}
      </div>

      {loading ? (
        <div className="text-bdu-cream/50 py-14 text-center">Loading survey data…</div>
      ) : total === 0 ? (
        <div className="border border-bdu-gold/10 py-16 text-center text-bdu-cream/50">
          No survey responses yet. Share{" "}
          <code className="text-bdu-gold">/credit-union</code> — the survey
          is embedded in the signup flow and will populate automatically.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-survey-grid">
          {SURVEY_QUESTIONS.map((q) => (
            <QuestionChart
              key={q.key}
              question={q}
              counts={counts[q.key] || {}}
              totalResponses={total}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HeadlineTile({ icon: Icon, label, value, sub, gold, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative border p-4 ${
        gold ? "border-bdu-gold/60 bg-bdu-gold/5" : "border-bdu-gold/20 bg-black/40"
      }`}
    >
      <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.26em] text-bdu-cream/55">
        <Icon size={11} className="text-bdu-gold" />
        {label}
      </div>
      <div
        className={`mt-3 font-serif text-3xl ${gold ? "text-bdu-gold" : "text-bdu-cream"}`}
      >
        {value}
      </div>
      <div className="mt-1 text-[11px] text-bdu-cream/55 leading-tight">{sub}</div>
    </div>
  );
}

function QuestionChart({ question, counts, totalResponses }) {
  const palette = ["#D4AF37", "#F7D878", "#B8860B", "#2EA44F", "#E32636"];

  // Build chart data in the SAME ORDER as the canonical options so the chart
  // is deterministic even when some options have 0 responses.
  const rows = question.options.map((opt, i) => {
    const count = counts[opt] || 0;
    return {
      option: opt,
      shortLabel: shortLabel(opt),
      count,
      pct: totalResponses ? Math.round((count / totalResponses) * 100) : 0,
      color: palette[i % palette.length],
    };
  });

  const top = rows.reduce((best, r) => (r.count > best.count ? r : best), rows[0]);

  return (
    <div
      className="border border-bdu-gold/15 bg-black/40 p-5"
      data-testid={`admin-survey-q-${question.key}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.26em] text-bdu-gold">
            Question {question.number} · {question.type === "checkbox" ? "Multi-select" : "Single-select"}
          </div>
          <h4 className="mt-1 font-serif text-base text-bdu-cream leading-snug">
            {question.label}
          </h4>
        </div>
      </div>

      <div className="mt-4" style={{ height: 32 * rows.length + 24 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 4, right: 24, left: 0, bottom: 0 }}
          >
            <XAxis type="number" hide domain={[0, Math.max(1, totalResponses)]} />
            <YAxis
              type="category"
              dataKey="shortLabel"
              width={170}
              tick={{ fill: "rgba(253,251,247,0.72)", fontSize: 11, fontFamily: "system-ui" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: "rgba(212,175,55,0.08)" }}
              contentStyle={{
                background: "#0A0A0A",
                border: "1px solid rgba(212,175,55,0.3)",
                fontSize: 11,
              }}
              labelStyle={{ color: "#D4AF37" }}
              formatter={(value, _n, p) => [`${value} (${p?.payload?.pct ?? 0}%)`, "Responses"]}
            />
            <Bar
              dataKey="count"
              radius={[0, 2, 2, 0]}
              label={{
                position: "right",
                fill: "rgba(253,251,247,0.85)",
                fontSize: 11,
                formatter: (v) => (v > 0 ? `${v}` : ""),
              }}
            >
              {rows.map((r) => (
                <Cell key={r.option} fill={r.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {top && top.count > 0 ? (
        <div className="mt-3 pt-3 border-t border-bdu-gold/10 text-[11px] text-bdu-cream/60 flex items-center justify-between flex-wrap gap-2">
          <span>
            Top:{" "}
            <span className="text-bdu-cream font-semibold">
              {shortLabel(top.option)}
            </span>{" "}
            — {top.count} ({top.pct}%)
          </span>
          <span className="text-bdu-cream/40">
            {totalResponses} total {question.type === "checkbox" ? "respondents" : "votes"}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Trim long canonical option labels for the chart Y-axis without losing meaning. */
function shortLabel(opt) {
  if (!opt) return "";
  // Common patterns in the NCUA survey: pull the first clause before "(", ",", ":", "—", or "/"
  const first = opt.split(/\s*[—–\-,:(]\s*/)[0].trim();
  if (first.length > 36) return first.slice(0, 34) + "…";
  return first;
}

function computeHeadline(counts, total) {
  const sum = (field, options) =>
    options.reduce((n, opt) => n + (counts?.[field]?.[opt] || 0), 0);

  const likelyCount = sum("join_likelihood", ["Very Likely", "Somewhat Likely"]);
  const inadequateCount = counts?.bank_satisfaction?.["No, inadequately served"] || 0;
  const missionCount =
    counts?.primary_reason?.["Mission-driven investment back into the local community"] || 0;

  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return {
    likelyCount,
    likelyPct: pct(likelyCount),
    inadequateCount,
    inadequatePct: pct(inadequateCount),
    missionCount,
    missionPct: pct(missionCount),
  };
}
